/** English content of the home (translation of LANDING_FR). */
import type { LandingData } from "./landingDict";

export const LANDING_EN: LandingData = {
  pageTitle: "Car rental and private driver in Cannes | KS Rent Services",
  hero: {
    driverTitle: "Booking",
    driverDescription:
      "A high-end private chauffeur service for all your business and personal journeys",
    driverCta: "Book now",
    rentalTitle: "Car rental",
    rentalDescription:
      "A fleet of high-end vehicles to meet every mobility need",
    rentalCta: "Discover our vehicles",
    driverImageAlt: "KS Rent private chauffeur service",
    rentalImageAlt: "KS Rent car rental",
    scrollHint: "Scroll down",
  },
  clients: {
    heading: "They trust us",
    divider: "Our clients",
    reviewsHeading: "Client reviews",
    reviews: [
      {
        quote:
          "Service level outstanding. The clients and I had an exceptional time and the customer service was excellent.",
        author: "Karen O. - Fashion director",
      },
      {
        quote:
          "Flawless coordination, absolute discretion, and perfect punctuality across several confidential journeys.",
        author: "Member of a Saudi royal family",
      },
      {
        quote:
          "A proactive team, immaculate vehicles, and a premium experience from first contact to final drop-off.",
        author: "Event partnerships manager",
      },
    ],
  },
  about: {
    heading: "Who we are",
    intro:
      "Based in Cannes since 2018, we look after public figures, companies and discerning travellers across the entire French Riviera. Our craft: an impeccable high-end fleet and hand-picked chauffeurs for a fully tailored experience.",
    cta: "About KS Rent Services",
    items: [
      {
        icon: "mdi:star-outline",
        title: "Service excellence",
        desc: "5★ standards, a refined welcome protocol and absolute discretion.",
      },
      {
        icon: "mdi:map-marker-radius-outline",
        title: "Cannes & Riviera",
        desc: "Operating across the whole French Riviera: Nice, Monaco, Saint-Tropez…",
      },
      {
        icon: "mdi:steering",
        title: "High-end fleet",
        desc: "Premium vehicles maintained and equipped for your comfort.",
      },
    ],
  },
  services: {
    eyebrow: "Cannes • French Riviera",
    heading: "Two areas of expertise, one standard of excellence",
    intro:
      "Two ways to travel. With a private chauffeur, a professional driver takes you in our vehicles: you enjoy the ride while we handle the road, the waiting and the luggage. With car rental, you drive a premium vehicle yourself, delivered to the address of your choice.",
    driverTitle: "Private chauffeur",
    driverBadge: "5★ service",
    driverLead:
      "Hourly hire, airport/station transfers, events and exceptional journeys. Discretion, punctuality and absolute comfort.",
    driverSub:
      "A service run with a dedicated driver for your transfers, events and hourly hire.",
    driverItems: ["Airport & Stations", "Hourly hire", "Events", "Companies"],
    driverCitiesLabel: "By city",
    driverCities: [
      "Private driver in Cannes",
      "Chauffeur in Antibes",
      "Driver in Nice",
      "Private chauffeur in Monaco",
      "Driver in Saint-Tropez",
    ],
    driverCtaPrimary: "Book now",
    driverCtaSecondary: "Learn more",
    rentalTitle: "Car rental",
    rentalBadge: "Luxury & Prestige",
    rentalLead:
      "Premium fleet, delivery/collection, tailored insurance and 24/7 assistance. The luxury experience, behind the wheel.",
    rentalSub:
      "Premium vehicles handed over for your own journeys, with bespoke support.",
    rentalItems: [
      "Prestige",
      "SUVs & Saloons",
      "Convertibles",
      "Delivery/Collection",
    ],
    rentalCtaPrimary: "Discover our vehicles",
    rentalCtaSecondary: "Bespoke quote",
    note: "Available in Cannes and across the whole French Riviera. Event coordination, multi-vehicle set-ups and special requirements on request.",
  },
  gallery: {
    eyebrow: "Our fleet",
    heading: "Vehicle gallery",
    intro:
      "Iconic supercars, exceptional saloons, luxury SUVs and convertibles. Discover a selection of vehicles available to rent across the whole French Riviera.",
    bullets: [
      "Rolls Royce & Maybach",
      "Lamborghini & Ferrari",
      "Porsche & Aston Martin",
      "Mercedes & Range Rover",
    ],
    ctaPrimary: "See the whole fleet",
    ctaSecondary: "Special request",
    prevLabel: "Previous image",
    nextLabel: "Next image",
    pauseLabel: "Pause",
    resumeLabel: "Resume the slideshow",
    dotLabel: (n) => `View image ${n}`,
    carFallbackAlt: "Vehicle",
  },
  excursions: {
    heading: "Our most popular excursions",
    discover: "Discover",
    cardTitles: [
      "Nice, Monaco",
      "Cannes, Gourdon",
      "Antibes, Juan-les-Pins",
      "Saint-Tropez",
      "Menton, Italian Riviera",
      "Lake St-Cassien, Verdon Gorge",
      "Èze, La Turbie",
      "Saint-Paul-de-Vence",
    ],
  },
  enterprise: {
    eyebrow: "Corporate support",
    heading: "Companies & Events",
    text: "Need a bespoke set-up, a coordinator or several vehicles? Our team designs and runs your turnkey mobility solutions.",
    ctaContact: "Contact us",
    ctaReserve: "Book a chauffeur",
  },
  valueProps: {
    eyebrow: "The choice of the most demanding clients",
    heading: "The KS Rent Services promise",
    intro:
      "Services designed for public figures, agents and companies: flexibility, security and service excellence.",
    reviewsEyebrow: "Anonymised client reviews",
    googleEyebrow: "Google reviews",
    googleNote: "Excellence confirmed by demanding clients.",
    catalogueLabel: "Catalogue",
    modelsAvailable: (n) => `${n}+ models available`,
    reviews: [
      {
        quote:
          "Premium coordination over several days, with no operational friction.",
        source: "Fashion house (anonymised)",
        tag: "Coordination",
      },
      {
        quote:
          "Consistent punctuality and flawless execution on every transfer.",
        source: "International leadership (anonymised)",
        tag: "Punctuality",
      },
      {
        quote:
          "Total discretion, perfect vehicle quality and a highly responsive team.",
        source: "VIP profile (anonymised)",
        tag: "Discretion",
      },
      {
        quote:
          "Masterful logistics management even under tight scheduling constraints.",
        source: "Event agency (anonymised)",
        tag: "Logistics",
      },
    ],
    cards: [
      {
        title: "Simple payment",
        description: "Card, transfer and corporate. Fast validation.",
        label: "Payment",
      },
      {
        title: "24/7 availability",
        description:
          "Continuous assistance, quick replies and real-time tracking.",
        label: "Support",
      },
      {
        title: "100% satisfaction",
        description:
          "Feedback from demanding brands and clients, in anonymised form.",
        label: "Experience",
      },
      {
        title: "Prestige fleet",
        description: "Saloons, SUVs, convertibles — elevated standards.",
        label: "Vehicles",
      },
      {
        title: "Events",
        description: "Multi-vehicle coordination & VIP set-ups.",
        label: "Operations",
      },
      {
        title: "Discretion & Security",
        description: "Strict protocols, complete confidentiality.",
        label: "Trust",
      },
    ],
    fleetAlt: "KS Rent Services fleet",
    eventsAlt: "Event atmosphere",
  },
  universe: {
    heading: "The KS Rent Services world",
    intro:
      "Private chauffeur, client moments and the fleet universe: an instant read on our service standards on the Riviera.",
    brandFilmAlt:
      "KS Rent Services brand film with private chauffeur and premium fleet",
    socialProofAlt: "Client setting and premium KS Rent Services service",
    fleetUniverseAlt: "Premium KS Rent Services fleet universe",
  },
  orient: {
    heading: "Point your request in the right direction",
    links: [
      { label: "Book a chauffeur", frPath: "/chauffeur" },
      { label: "Explore private excursions", frPath: "/excursions" },
      {
        label: "Rent a vehicle",
        frPath: "/location/vehicules#catalogue-location",
      },
      { label: "Quick request", frPath: "/reservation" },
    ],
  },
};
